import { useQueryClient } from "@tanstack/react-query";
import { CalculationForm } from "features/calculation/CalculationForm";
import {
  useFeeBasisAsTransactionCalculationRetrieveSuspense,
  useFeeBasisAsTransactionCalculationUpdate,
} from "gen/endpoints/fee-basis/fee-basis";
import { getTransactionCalculationAndFeeBasisListQueryKey } from "gen/endpoints/transactions/transactions";
import { CalculationTypeEnum } from "gen/model/calculationTypeEnum";
import { TransactionCalculationPayloadCreateDataRequest } from "gen/model/transactionCalculationPayloadCreateDataRequest";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

export const FeeBasisEditPage: React.FC = () => {
  const { t } = useTranslation(["calculation"]);
  const queryClient = useQueryClient();

  const { fundFamilyId, feeBasisId } = useRequiredStringParams();

  const { data: transactionCalculationData } =
    useFeeBasisAsTransactionCalculationRetrieveSuspense(
      fundFamilyId,
      feeBasisId,
    );

  const { mutateAsync: updateFeeBasisViaCalculation } =
    useFeeBasisAsTransactionCalculationUpdate();

  const onSubmit = useCallback(
    async (data: TransactionCalculationPayloadCreateDataRequest) => {
      await updateFeeBasisViaCalculation({
        feeBasisId,
        fundFamilyId,
        data: {
          ...data,
          calculation_type: CalculationTypeEnum.advanced,
        },
      });
      await queryClient.invalidateQueries({
        queryKey:
          getTransactionCalculationAndFeeBasisListQueryKey(fundFamilyId),
      });
    },
    [updateFeeBasisViaCalculation, feeBasisId, fundFamilyId, queryClient],
  );

  const updatedObject = {
    ...transactionCalculationData.transaction_calculation,
    classification: "fee_basis",
    calculation_type: CalculationTypeEnum.advanced,
  };

  return (
    <PageContent>
      <PageHeader size="lg" title={t("calculation:editCalculation")} />
      <PageBody>
        <CalculationForm
          fundFamilyId={fundFamilyId}
          transactionCalculationId={feeBasisId}
          onSubmit={onSubmit}
          defaultValues={updatedObject as never}
        />
      </PageBody>
    </PageContent>
  );
};
